<template>
  <b-form-group
    :class="formGroupClass"
    :label-for="makeId('day')"
    :state="state"
    :invalid-feedback="invalid"
    :description="description"
  >
    <template v-slot:label>
      <div class="d-flex align-items-center">
        <EHQFieldLabel :label="label" />
        <HelpReferenceWrapper
          v-if="helpReferenceIdentifier"
          :identifier="helpReferenceIdentifier"
        />
      </div>
    </template>
    <div class="row">
      <div :class="inputClass">
        <b-select
          :id="makeId('day')"
          v-model="data.day"
          autocomplete="nope"
          :name="makeName('day')"
          :state="state"
          :options="dayOptions"
          :disabled="disabled"
          :size="size"
          @change="onChange()"
        />
      </div>

      <div :class="inputClass">
        <b-select
          :id="makeId('month')"
          v-model="data.month"
          autocomplete="nope"
          :name="makeName('month')"
          :state="state"
          :options="monthOptions"
          :disabled="disabled"
          :size="size"
          @change="onChange()"
        />
      </div>

      <div :class="inputClass">
        <b-select
          :id="makeId('year')"
          v-model="data.year"
          autocomplete="nope"
          :name="makeName('year')"
          :state="state"
          :options="yearOptions"
          :disabled="disabled"
          :size="size"
          @change="onChange()"
        />
      </div>
    </div>
  </b-form-group>
</template>

<script>
import * as _ from "lodash-es";

import { isObjectValuesEmpty } from "@/utils";
import { parseISO } from "date-fns";
import FormFieldsMixin from "@/mixins/FormFieldsMixin";
import { MONTHES } from "@/constants";

export default {
  name: "SelectDatepicker",
  mixins: [FormFieldsMixin],
  props: {
    value: String,
    initialDate: Date,
    invalid: String,
    years: {
      required: false,
      type: Array,
    },
    state: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    prefix: String,
    label: String,
    helpReferenceIdentifier: String,
    description: String,
    size: String,
    disabled: Boolean,
    inline: {
      type: Boolean,
      required: false,
      default: true,
    },
    formGroupClass: {
      type: String,
      required: false,
      default: "",
    },
  },
  emits: ["input"],
  data() {
    const dateStr = this.value || (this.initialDate && this.initialDate.toISOString());
    return { data: this.parseDateString(dateStr) };
  },
  computed: {
    dayOptions() {
      return [{ text: "--- Day ---", value: null }, ..._.range(1, 32)];
    },
    monthOptions() {
      return [
        { text: "--- Month ---", value: null },
        ..._.range(0, 12).map((n) => ({ text: MONTHES[n].substr(0, 3), value: n + 1 })),
      ];
    },
    yearOptions() {
      const year = new Date().getFullYear();
      return [
        { text: "--- Year ---", value: null },
        ...(this.years || _.range(year + 1, year - 100)),
      ];
    },
    inputClass() {
      return { "col-md-4": this.inline, "col col-12": true };
    },
  },
  watch: {
    value(newValue, oldValue) {
      if (newValue && !oldValue && isObjectValuesEmpty(this.data)) {
        // Replace empty data with new data when load profile.
        this.data = this.parseDateString(newValue);
      }
      if (!newValue && oldValue) {
        // Clear inputs
        this.data = this.parseDateString(newValue);
      }
    },
  },
  methods: {
    parseDateString(dt) {
      const empty = {
        year: null,
        month: null,
        day: null,
      };
      if (!dt) {
        return empty;
      }
      const date = parseISO(dt);
      if (isNaN(date.getTime())) {
        return empty;
      }
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      };
    },
    /**
     * Return ISO-formatted date if year month and date are selected alltogether,
     * and null otherwise to prevent showing format errors from backend to user
     * and to mark that date is not filled yet.
     *
     * @returns {String|null}
     */
    format() {
      const { year, month, day } = this.data;
      if (year && month && day) {
        return `${year}-${month > 9 ? month : "0" + month}-${day > 9 ? day : "0" + day}`;
      }
      return null;
    },
    onChange() {
      this.$nextTick(() => {
        this.$emit("input", this.format());
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.row {
  // Decrease default size to fit into print view.
  margin-left: -5px !important;
  margin-right: -5px !important;

  .col {
    padding-left: 5px !important;
    padding-right: 5px !important;
    margin-bottom: 0.25rem !important;
  }
}
</style>
